/**
 * Pure Q&A ordering helpers for the thread panel, extracted from
 * `ThreadPanel.tsx` so the behavior is testable in isolation.
 *
 * In a PUBLIC community channel a thread can have an accepted answer
 * (`accepted_answer_id` on the root message's payload). When set — and the
 * referenced reply still exists — that reply floats to the top of the list
 * and the thread reads as "solved". The accepted answer can be HARD-deleted
 * out from under the payload, leaving a dangling id, so callers must guard on
 * the reply actually still existing.
 */

/** Minimal shape these helpers need from a thread reply row. */
export interface OrderableReply {
  id: string;
}

/**
 * Whether the accepted-answer reply referenced by the payload still exists in
 * the loaded reply set. The id can dangle after a hard delete; only treat the
 * thread as solved / float the answer when the reply is actually present.
 */
export function acceptedReplyExists<T extends OrderableReply>(
  acceptedAnswerId: string | null,
  replies: readonly T[],
): boolean {
  return !!acceptedAnswerId && replies.some((r) => r.id === acceptedAnswerId);
}

/**
 * Reorder replies so the accepted answer floats to the top (public workspaces
 * only). Returns the original array reference when there's nothing to reorder
 * — no accepted id, not a public workspace, or the accepted reply isn't in the
 * set — so memo identity is preserved exactly as the original inline logic.
 *
 * Accepts a `readonly` array (the Zero composite's `thread_replies` slice is
 * readonly) and returns the same readonly element type.
 */
export function orderThreadReplies<T extends OrderableReply>(
  replies: readonly T[],
  acceptedAnswerId: string | null,
  isPublicWs: boolean,
): readonly T[] {
  if (!isPublicWs || !acceptedAnswerId) return replies;
  const accepted = replies.filter((r) => r.id === acceptedAnswerId);
  if (accepted.length === 0) return replies;
  return [...accepted, ...replies.filter((r) => r.id !== acceptedAnswerId)];
}
